import { useState, useCallback } from 'react';
import API_ROUTES from './apiRoutes';
import { Series } from '../Models/Series';
import { useFetch } from './useFetch';

export const useSeries = () => {
    const [series, setSeries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { fetchWithAuth } = useFetch();

    const getToken = () => localStorage.getItem("token");

    const fetchSeries = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetchWithAuth(API_ROUTES.SERIES.list(), {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${getToken()}`,
                    "Content-Type": "application/json",
                }
            });
            if (!response.ok) {
                throw new Error('Falha ao carregar séries');
            }
            const data = await response.json();
            const transformedSeries = data.series.map(serie => Series.fromAPI(serie));
            setSeries(transformedSeries);
            setError(null);
        } catch (err) {
            setError(err.message || 'Erro ao carregar séries');
        } finally {
            setLoading(false);
        }
    }, []);

    const createSeries = useCallback(async (seriesData) => {
        try {
            setLoading(true);
            const series = new Series(seriesData);
            const response = await fetch(API_ROUTES.SERIES.create(), {
                method: 'POST',
                headers: {
                    "Authorization": `Bearer ${getToken()}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(series.toAPI())
            });

            if (!response.ok) {
                throw new Error('Falha ao criar série');
            }

            await fetchSeries();
            return true;
        } catch (err) {
            setError(err.message || 'Erro ao criar série');
            return false;
        } finally {
            setLoading(false);
        }
    }, [fetchSeries]);

    const updateSeries = useCallback(async (seriesId, seriesData) => {
        try {
            setLoading(true);
            const series = new Series({ id: seriesId, ...seriesData });
            const response = await fetch(API_ROUTES.SERIES.update(seriesId), {
                method: 'PUT',
                headers: {
                    "Authorization": `Bearer ${getToken()}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(series.toAPI())
            });

            if (!response.ok) {
                throw new Error('Falha ao atualizar série');
            }

            await fetchSeries();
            return true;
        } catch (err) {
            setError(err.message || 'Erro ao atualizar série');
            return false;
        } finally {
            setLoading(false);
        }
    }, [fetchSeries]);

    const deleteSeries = useCallback(async (seriesId) => {
        try {
            setLoading(true);
            const response = await fetch(API_ROUTES.SERIES.delete(seriesId), {
                method: 'DELETE',
                headers: {
                    "Authorization": `Bearer ${getToken()}`,
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error('Falha ao excluir série');
            }

            await fetchSeries();
            return true;
        } catch (err) {
            setError(err.message || 'Erro ao excluir série');
            return false;
        } finally {
            setLoading(false);
        }
    }, [fetchSeries]);

    const fetchSerieWithEpisodes = useCallback(async (serieId) => {
        setLoading(true);
        try {
            const response = await fetch(API_ROUTES.SERIES.get(serieId), {
                method: 'GET',
                headers: {
                    "Authorization": `Bearer ${getToken()}`,
                    "Content-Type": "application/json",
                }
            });
            if (!response.ok) {
                throw new Error('Falha ao carregar série');
            }
            const data = await response.json();
            return data;
        } catch (err) {
            setError(err.message || 'Erro ao carregar série');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        series,
        loading,
        error,
        createSeries,
        updateSeries,
        deleteSeries,
        refreshSeries: fetchSeries,
        fetchSerieWithEpisodes
    };
};